import usePageStore from '~/stores/page';
import articlesConfig, { type IArticlesConfig } from '~~/configs/articles.config';
import type { TArticle, TArticleType } from '~~/types/local_types';

export default async function useArticle(articleType: TArticleType): Promise<TArticle | undefined> {
  const { objectKeysToCamelCase } = formatUtils();
  const cms = useCms();
  const { slug } = useRoute().params;
  const { setCurrentPage } = usePageStore();
  const cacheKey = `articles-${articleType}-${slug}`;
  const cachedItem = useNuxtData(cacheKey).data.value;

  /* Fetch article data with error handling
    id: Article id
    fields: the data we need
  */
  const fetchPage = async (id: number | string, fields: (string | object)[] = ['*']) => {
    return await cms.readItem('articles', id, { fields });
  };

  // Retrieve cached item or fetch data
  let item: TArticle | undefined = cachedItem
    ? (objectKeysToCamelCase(cachedItem) as TArticle)
    : undefined;
  const fallbackTypeDefinition = articlesConfig.artikelen as IArticlesConfig;
  const articleTypeDefinition = articlesConfig[articleType] || fallbackTypeDefinition;
  const { fields, notFound, archived } = articleTypeDefinition;

  if (!item) {
    // Extract article ID from slug

    const rawSlug = Array.isArray(slug) ? slug.at(-1) : slug;
    const id = (rawSlug || '').split('-').at(-1);
    if (!id || isNaN(parseInt(id, 10))) {
      throwError(notFound.statusCode, notFound.statusMessage, notFound.statusMessage);
      return;
    }

    // Attempt to fetch data using Nuxt's useAsyncData or fallback to Directus SDK
    const { data } = await useAsyncData(cacheKey, () => fetchPage(id, fields));
    item = data.value ? (objectKeysToCamelCase(data.value) as TArticle) : undefined;

    if (!item) {
      if (import.meta.dev) {
        console.info('Retry Directus SDK.');
      }
      try {
        item = (await fetchPage(id, fields)) as TArticle | undefined;
        item = item ? objectKeysToCamelCase(item) : undefined;
      } catch (error) {
        throwFetchError(error, notFound || fallbackTypeDefinition.notFound);
      }
    }
  }

  if (item?.status === 'archived') {
    const { statusCode, statusMessage, message } = archived || fallbackTypeDefinition.archived;
    throwError(statusCode, statusMessage, message);
  }

  // Set the current page in the store and return the article
  setCurrentPage(item);
  return item;
}
